/**
 * Error handling facade for the dashboard.
 *
 * The error *model* (codes, severities, retryability, wire envelope, user-copy
 * resolution) is owned by `@authe/fe-commons/errors` + `@authe/ui-core/notify`.
 * This module re-exports that surface so feature code has ONE import, and adds
 * the handful of dashboard-only error situations that aren't backend codes.
 */
export { toErrorResponse, AutheError, getCodeSpec } from "@authe/fe-commons/errors";
export type { ErrorResponse, ErrorBody } from "@authe/fe-commons/errors";
export { notifyError, notifySuccess, notifyInfo, notifyWarning } from "@authe/ui-core/notify";

/** Dashboard-only, client-side error situations (not part of the API catalog). */
export const DASHBOARD_ERRORS = {
  SESSION_LOOP: {
    title: "Sign-in could not complete",
    description:
      "We detected a redirect loop with the login site. Clear your cookies for this domain and try again.",
  },
  /**
   * A sign-in that came back refused (e.g. the post-login Action denied because
   * the org was removed) — distinct from SESSION_LOOP, which is only the
   * circuit breaker tripping. An expired session never lands here: it just
   * redirects to the login page.
   */
  SESSION_ENDED: {
    title: "You're signed out",
    description: "Your session has ended. Sign in again to continue.",
  },
  /**
   * The API refused the open session with `PERM005`: its organization
   * membership was removed or deactivated, or its organization was closed.
   * Terminal and app-wide — see `app/AccessEndedGate.tsx`. Distinct from
   * SESSION_ENDED, which is a refused sign-in, not a session cut off mid-use.
   */
  ACCESS_ENDED: {
    title: "Your access to this workspace has ended",
    description: "Your membership in this organization was removed, or the organization was closed.",
  },
  /** ACCESS_ENDED for a demo account (the token carries `demo_expires_at`). */
  DEMO_ENDED: {
    title: "This demo has ended",
    description: "This demo workspace has closed and its sample data is being removed.",
  },
  NO_TENANT: {
    title: "No active tenant",
    description: "Your session has no tenant context. Pick a tenant or contact your administrator.",
  },
  FEATURE_DISABLED: {
    title: "Not available on your plan",
    description: "This feature is not enabled for your organization.",
  },
} as const;

export type DashboardErrorKey = keyof typeof DASHBOARD_ERRORS;
